import { KVAnnotation, KVPrimitive } from 'types'
import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import { parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

/**
 * A join table entry that connects a kestrel-model to a unit.
 * This allows for many-to-many relationships between kestrel models and units.
 *
 * The data field can contain additional metadata about the relationship,
 * such as which annotation or primitive connects them.
 */
export class GeckoKestrelModelUnit extends GeckoEntryBase<
  {
    annotation?: KVAnnotation
    primitive?: KVPrimitive
  },
  {
    organization: string
    site: string
    [RolodexEntryType.KESTREL_MODEL]: string
    [RolodexEntryType.UNIT]: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    const organization = parseString(json.tags.organization)
    const site = parseString(json.tags.site)
    const kestrelModel = parseString(json.tags[RolodexEntryType.KESTREL_MODEL])
    const unit = parseString(json.tags[RolodexEntryType.UNIT])

    if (site === undefined) {
      throw new Error(
        `GeckoKestrelModelUnit: site tag is required but was undefined (id: ${json.id})`
      )
    }
    if (kestrelModel === undefined) {
      throw new Error(
        `GeckoKestrelModelUnit: ${RolodexEntryType.KESTREL_MODEL} tag is required but was undefined (id: ${json.id})`
      )
    }
    if (unit === undefined) {
      throw new Error(
        `GeckoKestrelModelUnit: ${RolodexEntryType.UNIT} tag is required but was undefined (id: ${json.id})`
      )
    }

    super(
      json,
      {
        annotation: json.data.annotation
          ? KVAnnotation.fromJSON(json.data.annotation)
          : undefined,
        primitive: json.data.primitive
          ? KVPrimitive.fromJSON(json.data.primitive)
          : undefined,
      },
      {
        organization: organization ?? '',
        site,
        [RolodexEntryType.KESTREL_MODEL]: kestrelModel,
        [RolodexEntryType.UNIT]: unit,
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson): GeckoKestrelModelUnit {
    return new GeckoKestrelModelUnit(json)
  }
}

export type KestrelModelUnit = GeckoKestrelModelUnit
